import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTheme } from '../../contexts/ThemeContext';
import { storage } from '../../firebase';
import {
  createPost,
  subscribeToPosts,
  getUserDataForPost,
} from '../../services/firebase/community';
import {
  COMMUNITY_PILLAR_LABELS,
  getPostAuthorAvatarUrl,
} from '../../utils/communityConstants';
import CommunityNewPostModal from './CommunityNewPostModal';

const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/** Compact relative-time label ("2h", "3d", "Just now") from a Date/timestamp. */
const formatRelativeTime = (value) => {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return '';
  const diff = Date.now() - time;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const emptyPost = (category) => ({
  title: '',
  description: '',
  files: [],
  type: 'general',
  category,
});

/**
 * Compact, Facebook-style community card embedded on each department overview.
 * Shows a "What's on your mind?" prompt that opens the full New Post modal with
 * this department's pillar pre-selected, plus the latest posts for the pillar.
 */
const DepartmentCommunityWidget = ({ category, maxPosts = 2, className = '' }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const auth = getAuth();

  const [posts, setPosts] = useState([]);
  const [authorData, setAuthorData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newPost, setNewPost] = useState(() => emptyPost(category));
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);

  const deptLabel = COMMUNITY_PILLAR_LABELS[category] || 'Department';

  // Latest posts for this pillar (subscribeToPosts returns newest-first with author data resolved).
  useEffect(() => {
    const unsubscribe = subscribeToPosts((updatedPosts) => {
      setPosts(updatedPosts.filter((post) => post.category === category));
    });
    return () => unsubscribe();
  }, [category]);

  // Current user's display name + avatar for the composer prompt (matches how posts render).
  useEffect(() => {
    let cancelled = false;
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;
    (async () => {
      try {
        const data = await getUserDataForPost(uid);
        if (!cancelled) setAuthorData(data);
      } catch (error) {
        console.error('Error loading community author data:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.currentUser?.uid]);

  const latestPosts = useMemo(() => posts.slice(0, maxPosts), [posts, maxPosts]);

  const composerAvatar = getPostAuthorAvatarUrl(
    { author: authorData?.authorName, authorAvatar: authorData?.authorAvatar },
    40
  );

  const openModal = () => {
    setNewPost(emptyPost(category));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setNewPost(emptyPost(category));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      alert('Please select a valid file type (images, PDF, or text files).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }

    try {
      setUploadingFiles(true);
      const fileName = `community-post-${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `community-attachments/${fileName}`);
      const uploadResult = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(uploadResult.ref);
      setNewPost((prev) => ({
        ...prev,
        files: [...prev.files, { name: file.name, size: file.size, type: file.type, url }],
      }));
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeFile = (fileName) => {
    setNewPost((prev) => ({ ...prev, files: prev.files.filter((f) => f.name !== fileName) }));
  };

  const submitPost = async () => {
    if (isSubmittingPost) return;
    if (!newPost.title.trim() || !newPost.description.trim()) {
      alert('Please fill in both title and description');
      return;
    }
    if (uploadingFiles) {
      alert('Please wait for files to finish uploading');
      return;
    }
    try {
      setIsSubmittingPost(true);
      await createPost(newPost);
      closeModal();
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setIsSubmittingPost(false);
    }
  };

  const goToCommunity = () => navigate(`/portal/community?pillar=${category}`);

  return (
    <div
      className={`rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-4 ${className}`}
    >
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="text-base leading-none" aria-hidden="true">💬</span>
        <h2 className="font-semibold text-gray-800 dark:text-white">{deptLabel} Community</h2>
      </div>

      {/* Facebook-style composer prompt — opens the full New Post modal pre-set to this pillar */}
      <div
        className={`flex items-center gap-3 rounded-full border px-2 py-2 ${
          darkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <img
          src={composerAvatar}
          alt=""
          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
        />
        <button
          type="button"
          onClick={openModal}
          className={`flex-1 text-left text-sm rounded-full px-4 py-2 transition-colors ${
            darkMode
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {`Share a ${deptLabel} post...`}
        </button>
      </div>

      {/* Latest posts for this pillar */}
      <div className="mt-3 space-y-1">
        {latestPosts.length === 0 ? (
          <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-3">
            No {deptLabel.toLowerCase()} posts yet. Be the first to share!
          </p>
        ) : (
          latestPosts.map((post) => (
            <button
              key={post.id}
              type="button"
              onClick={goToCommunity}
              className={`w-full text-left flex gap-3 rounded-lg p-2 transition-colors ${
                darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
              }`}
            >
              <img
                src={getPostAuthorAvatarUrl(post, 32)}
                alt=""
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-gray-800 dark:text-white truncate">
                    {post.author}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {formatRelativeTime(post.createdAt)}
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-200 truncate">{post.title}</p>
                {post.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {post.description}
                  </p>
                )}
              </div>
            </button>
          ))
        )}
      </div>

      <div className="mt-2 text-center">
        <button
          type="button"
          onClick={goToCommunity}
          className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
        >
          View all in Community
        </button>
      </div>

      <CommunityNewPostModal
        isOpen={showModal}
        onClose={closeModal}
        newPost={newPost}
        setNewPost={setNewPost}
        onSubmit={submitPost}
        onFileChange={handleFileChange}
        onRemoveFile={removeFile}
        uploadingFiles={uploadingFiles}
        isSubmittingPost={isSubmittingPost}
      />
    </div>
  );
};

export default DepartmentCommunityWidget;
